import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

// Session storage keys for cross-system compatibility
const SESSION_KEY = "session_mode_data";
const SESSION_EXPIRY_KEY = "session_mode_expiry";
const SESSION_DURATION_MS = 4 * 60 * 60 * 1000; // 4 hours

// CartContext compatible keys (legacy fallback)
const CART_ORDER_TYPE_KEY = "orderType";
const CART_TABLE_NUMBER_KEY = "tableNumber";
const CART_LOCATION_CODE_KEY = "locationCode";
const CART_SESSION_MODE_KEY = "session_mode";

/**
 * SessionBootstrap - Global component that captures and consolidates
 * session parameters from URL on every route load.
 * 
 * This ensures that ?mesa=X, ?type=pickup, and ?store=code are always
 * captured regardless of which page the user lands on (QR scans).
 */
const SessionBootstrap = () => {
  const [searchParams] = useSearchParams();

  // Consolidate existing session on mount (runs only once)
  useEffect(() => {
    const sessionData = sessionStorage.getItem(SESSION_KEY);
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        // Ensure legacy keys are also set for compatibility
        if (parsed.mode === "mesa" && parsed.tableNumber) {
          sessionStorage.setItem(CART_ORDER_TYPE_KEY, "mesa");
          sessionStorage.setItem(CART_TABLE_NUMBER_KEY, parsed.tableNumber.toString());
          sessionStorage.setItem(CART_SESSION_MODE_KEY, "mesa");
          if (parsed.locationCode) {
            sessionStorage.setItem(CART_LOCATION_CODE_KEY, parsed.locationCode);
          }
        } else if (parsed.mode === "pickup") {
          sessionStorage.setItem(CART_ORDER_TYPE_KEY, "llevar");
          sessionStorage.setItem(CART_SESSION_MODE_KEY, "pickup");
          if (parsed.locationCode) {
            sessionStorage.setItem(CART_LOCATION_CODE_KEY, parsed.locationCode);
          }
        }
        console.log("[SessionBootstrap] Session consolidated on mount:", parsed);
      } catch (e) {
        console.error("[SessionBootstrap] Error parsing session:", e);
      }
    }
  }, []); // Only on mount

  // Capture URL params on route changes
  useEffect(() => {
    const mesaParam = searchParams.get("mesa");
    const typeParam = searchParams.get("type");
    const storeParam = searchParams.get("store") || searchParams.get("sede");

    // Only process if we have relevant params
    if (!mesaParam && !typeParam) return;

    const expiry = Date.now() + SESSION_DURATION_MS;

    // Case A: Mesa parameter (table QR)
    if (mesaParam) {
      const mesaNumber = parseInt(mesaParam, 10);
      if (!isNaN(mesaNumber) && mesaNumber >= 1 && mesaNumber <= 99) {
        // New session model
        const sessionData = {
          mode: "mesa",
          tableNumber: mesaNumber,
          locationCode: storeParam || null,
        };
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
        sessionStorage.setItem(SESSION_EXPIRY_KEY, expiry.toString());

        // CartContext compatible keys (fallback)
        sessionStorage.setItem(CART_ORDER_TYPE_KEY, "mesa");
        sessionStorage.setItem(CART_TABLE_NUMBER_KEY, mesaNumber.toString());
        sessionStorage.setItem(CART_SESSION_MODE_KEY, "mesa");
        if (storeParam) {
          sessionStorage.setItem(CART_LOCATION_CODE_KEY, storeParam);
        }

        console.log("[SessionBootstrap] Mesa mode activated:", sessionData);
        return;
      }
    }

    // Case B: Pickup/takeaway parameter (counter QR)
    if (typeParam === "pickup" || typeParam === "llevar") {
      // New session model
      const sessionData = {
        mode: "pickup",
        tableNumber: null,
        locationCode: storeParam || null,
      };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
      sessionStorage.setItem(SESSION_EXPIRY_KEY, expiry.toString());

      // CartContext compatible keys (fallback)
      sessionStorage.setItem(CART_ORDER_TYPE_KEY, "llevar");
      sessionStorage.removeItem(CART_TABLE_NUMBER_KEY);
      sessionStorage.setItem(CART_SESSION_MODE_KEY, "pickup");
      if (storeParam) {
        sessionStorage.setItem(CART_LOCATION_CODE_KEY, storeParam);
      }

      console.log("[SessionBootstrap] Pickup mode activated:", sessionData);
    }
  }, [searchParams]);

  // This component doesn't render anything
  return null;
};

export default SessionBootstrap;
